import type { IMouseEvent } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

interface EventMessage {
    events: IMouseEvent[];
    eventData: {
        modifiers: Record<'ctrl' | 'alt' | 'shift' | 'meta', boolean | undefined>;
        button: number;
    };
    locatedElement: ILocatedElement;
}
declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;

export const wheel = function (eventData: EventMessage, done: (data: { state: 'success' }) => void) {
    const MAX_WHEEL_EVENT_TIMEOUT = 200;

    function getEventPosition(userEvent: IMouseEvent, element: Element) {
        function isWithinBounds(start: number, end: number, point: number) {
            return (point > start) && (point < end);
        }
        const pointerPosition: Partial<IMouseEvent['pointerPosition']> = userEvent.pointerPosition || {};
        const rect = element.getBoundingClientRect();
        const clientX = pointerPosition.originX && isWithinBounds(rect.left, rect.left + rect.width, pointerPosition.originX) ? pointerPosition.originX : rect.left + (rect.width / 2);
        const clientY = pointerPosition.originY && isWithinBounds(rect.top, rect.top + rect.height, pointerPosition.originY) ? pointerPosition.originY : rect.top + (rect.height / 2);
        return { x: clientX, y: clientY };
    }

    function getWheelEvent(userEvent: IMouseEvent, _eventData: EventMessage['eventData'], element: Element) {
        const pos = getEventPosition(userEvent, element);
        const modifiers = _eventData?.modifiers || {};
        const dict = {
            deltaX: (userEvent as any).deltaX,
            deltaY: (userEvent as any).deltaY,
            deltaZ: (userEvent as any).deltaZ,
            deltaMode: (userEvent as any).deltaMode,
            clientX: pos.x,
            clientY: pos.y,
            bubbles: true,
            cancelable: true,
            ctrl: Boolean(modifiers.ctrl),
            alt: Boolean(modifiers.alt),
            shift: Boolean(modifiers.shift),
            meta: Boolean(modifiers.meta),
        };
        return new WheelEvent('wheel', dict);
    }

    function executeEvents(events: IMouseEvent[], element: Element | null) {
        if (events.length === 0) {
            done({ state: 'success' });
            return;
        }
        if (!element) {
            throw new Error('element not found');
        }

        const event = events.shift()!;
        const currentEvent = getWheelEvent(event, eventData.eventData, element);
        const timeout = events[0] ? Math.min(events[0].timeStamp! - event.timeStamp!, MAX_WHEEL_EVENT_TIMEOUT) : MAX_WHEEL_EVENT_TIMEOUT;
        element.dispatchEvent(currentEvent);
        setTimeout(() => {
            executeEvents(events, element);
        }, timeout);
    }

    const element = getLocatedElement(eventData.locatedElement);
    executeEvents(eventData.events, element);
};
